import PropTypes from 'prop-types'
import { useState } from 'react'
import IdeaStepDetail from './IdeaStepDetail'

const IdeaProgress = ({ currentIdea, formatDate, isExpanded = true, onToggle, onRefineIdea, onArchiveIdea, archiving = false, onProgressToMarketResearch, skipping = false, history = [] }) => {
  const [expandedStages, setExpandedStages] = useState({
    triage: false,
    demand: false,
    saturation: false,
    opportunity: false,
    marketOpportunity: false
  })

  const toggleStage = (stage) => {
    setExpandedStages(prev => {
      // If clicking on already expanded stage, collapse it
      if (prev[stage]) {
        return {
          triage: false,
          demand: false,
          saturation: false,
          opportunity: false,
          marketOpportunity: false
        }
      }
      // Otherwise, close all sections and open the selected one
      return {
        triage: stage === 'triage',
        demand: stage === 'demand',
        saturation: stage === 'saturation',
        opportunity: stage === 'opportunity',
        marketOpportunity: stage === 'marketOpportunity'
      }
    })
  }

  const getCurrentStage = () => {
    // Special handling for ideas that need refinement - they should stay at triage stage
    if (currentIdea?.status === 'idea-refinement') {
      return 'triage'
    }
    
    // Check if all assessments are complete - either by having the data OR by status
    if (currentIdea?.opportunity_assessment || currentIdea?.status === 'opportunity-assessed') {
      return 'completed'
    }
    
    // If status is opportunity-assessment or later, skip triage check
    // (user may have force-progressed without triage assessment)
    if (currentIdea?.status === 'opportunity-assessment' || 
        currentIdea?.status === 'search-terms-generated' ||
        currentIdea?.status === 'market-demand-evaluated' ||
        currentIdea?.status === 'market-saturation-evaluated' ||
        currentIdea?.status === 'usp-evaluated') {
      // Check which step we're on based on available data
      if (!currentIdea?.market_demand_evaluation) return 'demand'
      if (!currentIdea?.market_saturation_evaluation) return 'saturation'
      if (!currentIdea?.usp_evaluation) return 'opportunity'
      return 'marketOpportunity'
    }
    
    if (!currentIdea?.triage_assessment) return 'triage'
    if (!currentIdea?.market_demand_evaluation) return 'demand'
    if (!currentIdea?.market_saturation_evaluation) return 'saturation'
    if (!currentIdea?.usp_evaluation) return 'opportunity'
    return 'marketOpportunity'
  }

  const getStepStatus = (stepKey) => {
    const currentStage = getCurrentStage()
    
    // CRITICAL: Special handling for rejected ideas
    if (currentIdea?.status === 'rejected-offensive' || currentIdea?.status === 'rejected-test-idea') {
      if (stepKey === 'triage') {
        return 'rejected'
      } else {
        // All other steps should show as blocked when idea is rejected
        return 'blocked'
      }
    }
    
    // Special handling for when idea needs refinement
    if (currentIdea?.status === 'idea-refinement') {
      if (stepKey === 'triage') {
        return 'needsRefinement'
      } else {
        // All other steps should show as pending when refinement is needed
        return 'pending'
      }
    }
    
    // Special handling for when idea is under review (triage status)
    if (currentIdea?.status === 'triage') {
      if (stepKey === 'triage') {
        return 'inProgress'
      } else {
        // All other steps should show as pending when still under review
        return 'pending'
      }
    }
    
    // Special handling for force-progressed ideas (skipped triage)
    if ((currentIdea?.status === 'opportunity-assessment' || 
         currentIdea?.status === 'search-terms-generated' ||
         currentIdea?.status === 'market-demand-evaluated' ||
         currentIdea?.status === 'market-saturation-evaluated' ||
         currentIdea?.status === 'usp-evaluated') && 
        !currentIdea?.triage_assessment && 
        stepKey === 'triage') {
      // Triage was skipped/force-progressed - mark as completed
      return 'completed'
    }
    
    // Map step keys to stage names
    const stepStageMap = {
      'triage': 'triage',
      'demand': 'demand', 
      'saturation': 'saturation',
      'opportunity': 'opportunity',
      'marketOpportunity': 'marketOpportunity'
    }
    
    const stepStage = stepStageMap[stepKey]
    
    if (currentStage === 'completed') {
      return 'completed'
    } else if (currentStage === stepStage) {
      return 'inProgress'
    } else {
      // Check if this step is completed
      const stepCompleted = {
        'triage': !!currentIdea?.triage_assessment || 
                  (currentIdea?.status === 'opportunity-assessment' || 
                   currentIdea?.status === 'search-terms-generated' ||
                   currentIdea?.status === 'market-demand-evaluated' ||
                   currentIdea?.status === 'market-saturation-evaluated' ||
                   currentIdea?.status === 'usp-evaluated' ||
                   currentIdea?.status === 'opportunity-assessed'),
        'demand': !!currentIdea?.market_demand_evaluation,
        'saturation': !!currentIdea?.market_saturation_evaluation,
        'opportunity': !!currentIdea?.usp_evaluation,
        'marketOpportunity': !!currentIdea?.opportunity_assessment
      }
      
      return stepCompleted[stepKey] ? 'completed' : 'pending'
    }
  }

  const getCompletedSteps = () => {
    // CRITICAL: For rejected ideas, triage completion is 0%
    if (currentIdea?.status === 'rejected-offensive' || currentIdea?.status === 'rejected-test-idea') {
      return 0
    }
    
    let completed = 0
    // Count triage as completed if assessment exists OR if force-progressed
    if (currentIdea?.triage_assessment || 
        currentIdea?.status === 'opportunity-assessment' ||
        currentIdea?.status === 'search-terms-generated' ||
        currentIdea?.status === 'market-demand-evaluated' ||
        currentIdea?.status === 'market-saturation-evaluated' ||
        currentIdea?.status === 'usp-evaluated' ||
        currentIdea?.status === 'opportunity-assessed') completed++
    if (currentIdea?.market_demand_evaluation) completed++
    if (currentIdea?.market_saturation_evaluation) completed++
    if (currentIdea?.usp_evaluation) completed++
    if (currentIdea?.opportunity_assessment) completed++
    return completed
  }

  return (
    <div className="rounded-2xl bg-gradient-to-br from-gray-700/60 to-gray-700/60 shadow-none outline -outline-offset-1 outline-white/10 px-5 py-6 sm:px-6 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"></div>
      <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-cyan-900/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-cyan-900/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>
      
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
      {/* Header */}
      <button
        onClick={onToggle}
        className={`w-full lg:pointer-events-none flex items-center justify-between gap-3 relative z-10 text-white lg:hover:opacity-100 hover:opacity-80 transition-opacity ${isExpanded ? 'mb-6' : 'mb-0'}`}
      >
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center shadow-lg">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div className="text-left">
            <h3 className="font-display text-2xl md:text-2xl font-light tracking-tight text-white">My Progress</h3>
            <p className="text-sm text-gray-300">I'll keep you posted as I go...</p>
          </div>
        </div>
        <svg className={`lg:hidden w-6 h-6 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Overall Progress Bar */}
      <div className={`relative z-10 ${isExpanded ? 'mb-6' : 'mt-4 mb-0'}`}>
        <div className="flex justify-between text-base mb-2">
          <span className="font-medium text-gray-200">Overall Progress</span>
          <span className="text-cyan-400 font-medium">Completed Step {getCompletedSteps()} of 5</span>
        </div>
        <div className="w-full bg-gray-600 rounded-full h-2">
          <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 h-2 rounded-full transition-all duration-700" 
               style={{ width: `${(getCompletedSteps() / 5) * 100}%` }}></div>
        </div>
      </div>

      {isExpanded && (
      <>
      {/* Status Cards */}
      <div>
        
        {/* Stage 1 AI Triage Assessment - Wrapper */}
        {currentIdea && (
          <IdeaStepDetail
            step={currentIdea.triage_assessment}
            stepMet={currentIdea?.triage_assessment?.status === 'idea-refinement' ? currentIdea.triage_assessment : (currentIdea?.triage_assessment?.status === 'opportunity-assessment' ? null : currentIdea?.triage_assessment)}
            stepScore={currentIdea?.triage_assessment?.confidence * 10}
            stepCount="1"
            stepName="First, I review your idea's clarity"
            stepDescription="I check if there's enough clarity to start market research, or if it needs tweaking"
            stepGauge="Confidence"
            stepScoreType="Percentage"
            stepAnalysisTitle="Here's my thinking"
            stepReasoning={currentIdea.triage_assessment?.reasoning}
            currentIdea={currentIdea}
            expandedStage={expandedStages.triage}
            stageKey="triage"
            stepStatus={getStepStatus('triage')}
            toggleStage={toggleStage}
            onRefineIdea={onRefineIdea}
            onArchiveIdea={onArchiveIdea}
            archiving={archiving}
            onProgressToMarketResearch={onProgressToMarketResearch}
            skipping={skipping}
            history={history} />
        )}

        {/* Stage 2 - Market Demand Assessment - Wrapper */}
        {currentIdea && (
          <IdeaStepDetail
            step={currentIdea.market_demand_evaluation}
            stepMet={currentIdea?.market_demand_evaluation}
            stepScore={currentIdea?.market_demand_evaluation?.score}
            stepCount="2"
            stepName="Next, I research market demand"
            stepDescription="I investigate how much people want what you're offering"
            stepGauge="Demand"
            stepScoreType="HighMediumLow"
            stepAnalysisTitle="What I found about demand"
            stepReasoning={currentIdea.market_demand_evaluation?.reason}
            currentIdea={currentIdea}
            expandedStage={expandedStages.demand}
            stageKey="demand"
            stepStatus={getStepStatus('demand')}
            toggleStage={toggleStage} />
        )}

        {/* Stage 3 - Market Saturation Assessment - Wrapper */}
        {currentIdea && (
          <IdeaStepDetail
            step={currentIdea.market_saturation_evaluation}
            stepMet={currentIdea?.market_saturation_evaluation}
            stepScore={currentIdea?.market_saturation_evaluation?.score}
            stepCount="3"
            stepName="Next, I check the competition"
            stepDescription="I explore how crowded the market is with similar solutions"
            stepGauge="Saturation"
            stepScoreType="HighMediumLow"
            stepAnalysisTitle="What I found about competition"
            stepReasoning={currentIdea.market_saturation_evaluation?.reason}
            currentIdea={currentIdea}
            expandedStage={expandedStages.saturation}
            stageKey="saturation"
            stepStatus={getStepStatus('saturation')}
            invertColors={true}
            toggleStage={toggleStage} />
        )}

        {/* Stage 4 - Unique Selling Proposition (USP) */}
        {currentIdea && (
          <IdeaStepDetail
            step={currentIdea.usp_evaluation}
            stepMet={currentIdea?.usp_evaluation}
            stepScore={currentIdea?.usp_evaluation?.score}
            stepCount="4"
            stepName="Next, I find your edge"
            stepDescription="I discover what makes your idea unique and valuable"
            stepGauge="USP"
            stepScoreType="HighMediumLow"
            stepAnalysisTitle="What makes you different"
            stepReasoning={currentIdea.usp_evaluation?.reason}
            currentIdea={currentIdea}
            expandedStage={expandedStages.opportunity}
            stageKey="opportunity"
            stepStatus={getStepStatus('opportunity')}
            toggleStage={toggleStage} />
        )}

        {/* Stage 5 - Opportunity Assessment */}
        {currentIdea && (
          <IdeaStepDetail
            step={currentIdea.opportunity_assessment}
            stepMet={currentIdea?.opportunity_assessment}
            stepScore={currentIdea?.opportunity_assessment?.score}
            stepCount="5"
            stepName="Finally, I warp up my thoughts"
            stepDescription="I evaluate your idea's overall market potential, and share well as recommended next steps"
            stepGauge="Opportunity"
            stepScoreType="HighMediumLow"
            stepAnalysisTitle="My overall assessment"
            stepReasoning={currentIdea.opportunity_assessment?.reason}
            currentIdea={currentIdea}
            expandedStage={expandedStages.marketOpportunity}
            stageKey="marketOpportunity"
            stepStatus={getStepStatus('marketOpportunity')}
            toggleStage={toggleStage} />
        )}
      </div>
      </>
      )}
    </div>
  )
}

IdeaProgress.propTypes = {
  currentIdea: PropTypes.object,
  formatDate: PropTypes.func,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  onRefineIdea: PropTypes.func,
  onArchiveIdea: PropTypes.func,
  archiving: PropTypes.bool,
  onProgressToMarketResearch: PropTypes.func,
  skipping: PropTypes.bool,
  history: PropTypes.arrayOf(PropTypes.shape({
    status: PropTypes.string,
    timestamp: PropTypes.string,
    reasoning: PropTypes.string
  }))
}

export default IdeaProgress